import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GoogleMap,
  Marker,
} from "@react-google-maps/api";
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  fetchAreasByCityId,
  fetchCities,
  getNetworkPartnerByAreaCode,
  getPolygonDetailNormal,
} from "redux/actions/Master";
import { AxiosResponse } from "axios";
import { CityModel } from "types/models/CityModel";
import { AreaModel } from "types/models/AreaModel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface NetworkPartner {
  id: number;
  area_code: string;
  partner_code: string;
  partner_name: string;
}

const containerStyle = {
  width: '100%',
  height: '100vh',
};
const MapSeeComponent: React.FC = () => {
  const { isLoaded } = useOutletContext<{ isLoaded: boolean }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [polygonPaths, setPolygonPaths] = useState<Array<Array<{ lat: number; lng: number }>>>([]);
  const [polygonRefs, setPolygonRefs] = useState<google.maps.Polygon[]>([]);
  const [cities, setCities] = useState<CityModel[]>([]);
  const [areas, setAreas] = useState<AreaModel[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedAreaObject, setSelectedAreaObject] = useState<AreaModel | undefined>();
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pinCode, setPinCode] = useState<string>('');
  const [latitude, setLatitude] = useState<string | null>(null);
  const [longitude, setLongitude] = useState<string | null>(null);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? 'YOUR_BACKUP_API_KEY';
  const defaultCenter = { lat: 28.6139, lng: 77.209 };
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const bounds = new window.google.maps.LatLngBounds();
  const [networkPartner, setNetworkPartner] = useState<string>('');
  const [networkPartners, setNetworkPartners] = useState<NetworkPartner[]>([]);


  useEffect(() => {
    console.log('step_1_updated', networkPartners);
  }, [networkPartners]);

  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, []);

  useEffect(() => {
    const fetchCitiesData = async () => {
      try {
        const response = (await dispatch(fetchCities())) as unknown as AxiosResponse;
        if (response?.status === 200) {
          setCities(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };
    fetchCitiesData();
  }, [dispatch]);

  useEffect(() => {

    return () => {
      polygonRefs.forEach(polygon => {
        google.maps.event.clearInstanceListeners(polygon);
      });
    };
  }, [polygonRefs]);

  const handleCityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const cityName = event.target.value;
    setSelectedCity(cityName);
    setAreas([]);
    setSelectedArea('');
    setSelectedAreaObject(undefined);

    setNetworkPartner('');
    setNetworkPartners([]);

    const selectedCityData = cities.find(city => city.name === cityName);
    if (selectedCityData) {
      try {
        const areas = (await dispatch(fetchAreasByCityId(selectedCityData.id))) as unknown as AreaModel[];
        setAreas(areas);
      } catch (error) {
        console.error('Failed to fetch areas:', error);
      }
    }
  };


  const handleAreaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedArea(value);
    setSelectedAreaObject(undefined);

    setNetworkPartner('');
    setNetworkPartners([]);

    const areaObj = areas.find(area => area.areaId === Number(value));
    setSelectedAreaObject(areaObj);

    if (areaObj?.areaCode) {
      try {
        const response = await dispatch(getNetworkPartnerByAreaCode(areaObj.areaCode)) as unknown as AxiosResponse;
        if (response?.status === 200) {
          setNetworkPartners(response.data.data);
          console.log(response.data.data);
          console.log("step_1", networkPartners)
        } else {

          setNetworkPartners([]);
          console.log("step_2", networkPartners)
        }
      } catch (error) {
        console.error("Failed to fetch Network Partners:", error);
        setNetworkPartners([]);
        console.log("step_3", networkPartners)
      }
    } else {
      setNetworkPartners([]);
      console.log("step_4", networkPartners)
    }
  };




  const isAllCities = selectedCity === '-1';

  const handleGoClick = useCallback(async () => {
    if (!selectedCity || !mapRef.current) return;
    console.log(networkPartners);
    const map = mapRef.current;
    const areaParam = selectedAreaObject ? selectedAreaObject.area : '';
    const cityParam = selectedCity === '-1' ? '' : selectedCity;
    const address = selectedAreaObject
      ? `${selectedAreaObject.area}, ${selectedCity}`
      : selectedCity;

    try {
      polygonRefs.forEach(p => p.setMap(null));
      setPolygonRefs([]);
      setPolygonPaths([]);

      let polygonDataList: any[];
      if (selectedCity === '-1') {
        const allResults = await Promise.all(
          cities.map(city =>
            (dispatch(getPolygonDetailNormal('', city.name)) as unknown as Promise<any[]>).catch(() => [] as any[])
          )
        );
        polygonDataList = allResults.flat();
      } else {
        polygonDataList = await dispatch(
          getPolygonDetailNormal(areaParam, cityParam)
        ) as unknown as any[];
      }

      const newPolygons: google.maps.Polygon[] = [];
      const bounds = new window.google.maps.LatLngBounds();

      if (Array.isArray(polygonDataList) && polygonDataList.length > 0) {
        polygonDataList.forEach((polygonData) => {
          if (polygonData?.latlongarray && polygonData.latlongarray.trim() !== '') {
            try {
              const paths = JSON.parse(polygonData.latlongarray);
              const polygonGroups = Array.isArray(paths[0]) ? paths : [paths];

              polygonGroups.forEach((path: Array<{ lat: number; lng: number }>) => {
                const googleMapPath = path.map(coord => new window.google.maps.LatLng(coord.lat, coord.lng));
                const updatedNPName = networkPartners.map((partner: any) => partner.partner_name).join(', ')
                const polygon = new window.google.maps.Polygon({
                  paths: googleMapPath,
                  map,
                  editable: false,
                  draggable: false,
                  fillColor: '#ff0033',
                  fillOpacity: 0.3,
                  strokeColor: '#ff0033',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                });

                (polygon as any).meta = {
                  city: polygonData.societyid || 'N/A',
                  area: polygonData.area_name || 'N/A',
                  networkPartner: polygonData.partner_names || 'N/A',
                };
                console.log(updatedNPName)
                google.maps.event.addListener(polygon, 'mouseover', (event) => {
                  const meta = (polygon as any).meta;
                  const content = `
      <div>
        <strong>City:</strong> ${meta.city || 'N/A'}<br/>
        <strong>Area:</strong> ${meta.area || 'N/A'}<br/>
        <strong>Network Partner:</strong> ${meta.networkPartner || 'N/A'}
      </div>
    `;
                  infoWindowRef.current?.setContent(content);
                  infoWindowRef.current?.setPosition(event.latLng);
                  infoWindowRef.current?.open(map);
                });

                google.maps.event.addListener(polygon, 'mouseout', () => {
                  infoWindowRef.current?.close();
                });

                newPolygons.push(polygon);
                setPolygonPaths(prev => [...prev, path]);
                googleMapPath.forEach(point => bounds.extend(point));
              });

            } catch (jsonError) {
              console.error('Failed to parse polygon JSON:', polygonData.latlongarray, jsonError);
            }
          }
        });

        setPolygonRefs(newPolygons);

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      } else {
        console.warn('No polygons found from API');
      }
    } catch (error) {
      console.error('Error fetching polygons:', error);
    }

    if (isAllCities) return;

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
      );
      const data = await response.json();
      if (data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setLocation({ lat, lng });
        setLatitude(lat.toString());
        setLongitude(lng.toString());

        if (bounds.isEmpty()) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }
      } else {
        console.warn('No geocode results, using default center');
        if (bounds.isEmpty()) {
          map.panTo(defaultCenter);
          map.setZoom(15);
        }
      }
    } catch (error) {
      console.error('Error from geocode API:', error);
      if (bounds.isEmpty()) {
        map.panTo(defaultCenter);
        map.setZoom(15);
      }
    }
  }, [
    selectedCity,
    selectedAreaObject,
    dispatch,
    googleMapsApiKey,
    polygonRefs,
    networkPartners
  ]);



  return (
    <>
      <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
        <Box
          position="absolute"
          top={60}
          left={12}
          sx={{
            zIndex: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            p: 2,
            borderRadius: 1,
            boxShadow: 3,
            width: { xs: '90%', sm: '400px' },
            maxWidth: '600px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/master/manageViewPolygon')}
              sx={{ textTransform: 'none', color: 'black', padding: 0, minWidth: 'auto' }}
            />
            <Typography variant="h4" sx={{ mb: 0, ml: 1 }}>
              Find Your Locality
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ height: '50px', mb: 2 }}>
            <TextField
              label="Select City"
              value={selectedCity}
              onChange={handleCityChange}
              select
              fullWidth
              sx={{ '& .MuiInputBase-input': { height: '36px', padding: '8px' } }}
              SelectProps={{
                MenuProps: {
                  PaperProps: { sx: { maxHeight: '200px' } },
                },
              }}
            >
              <MenuItem key="-1" value="-1">All</MenuItem>
              {cities.map(city => (
                <MenuItem key={city.id} value={city.name}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>


          {selectedCity && (
            <Box display="flex" flexDirection="column" gap={2}>
              {!isAllCities && (
                <FormControl fullWidth sx={{ height: '36px' }}>
                  <TextField
                    label="Select Area"
                    value={selectedArea}
                    onChange={handleAreaChange}
                    select
                    fullWidth
                    sx={{ '& .MuiInputBase-input': { height: '36px', padding: '8px' } }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { maxHeight: '200px' } },
                      },
                    }}
                  >
                    {areas.map(area => (
                      <MenuItem key={area.areaId} value={area.areaId}>
                        {area.area}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}

              <Box display="flex" alignItems="center" gap={2}>
                {!isAllCities && (
                  <FormControl fullWidth sx={{ height: '50px' }}>
                    <TextField
                      label="Network Partner"
                      value={networkPartner}
                      onChange={(e) => setNetworkPartner(e.target.value)}
                      select
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-input': { height: '36px', padding: '8px' },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { sx: { maxHeight: '200px' } },
                        },
                      }}
                    >
                      {networkPartners.length > 0 ? (
                        networkPartners.map((np) => (
                          <MenuItem key={np.id} value={np.partner_name}>
                            {np.partner_name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No Network Partners Available</MenuItem>
                      )}
                    </TextField>
                  </FormControl>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  sx={{ height: '36px', whiteSpace: 'nowrap' }}
                  onClick={handleGoClick}
                >
                  Go
                </Button>
              </Box>
            </Box>
          )}

        </Box>

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={location || defaultCenter}
            zoom={15}
            onLoad={map => {
              mapRef.current = map;
            }}
          >
            {location && (
              <Marker
                position={location}
                icon={{
                  url: "/assets/images/Location-pin_Comway.svg",
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              />
            )}
          </GoogleMap>
        ) : (
          <Typography>Loading map...</Typography>
        )}

        <Snackbar
          open={errorSnackbar}
          autoHideDuration={6000}
          onClose={() => setErrorSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 9999 }}
        >
          <Alert
            onClose={() => setErrorSnackbar(false)}
            severity="error"
            sx={{ width: "100%" }}
          >
            Something went wrong. Please try again!
          </Alert>
        </Snackbar>

        <Snackbar
          open={successSnackbar}
          autoHideDuration={3000}
          onClose={() => setSuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 9999 }}
        >
          <Alert
            onClose={() => setSuccessSnackbar(false)}
            severity="success"
            sx={{
              width: '100%',
              bgcolor: '#4caf50',
              color: '#ffffff',
              fontWeight: 'bold',
            }}
          >
            Polygon Created Successfully
          </Alert>
        </Snackbar>
      </Box>

    </>


  );
};

export default MapSeeComponent;